import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import EvidenceTypesHelp from './EvidenceTypesHelp.jsx';
import TrashIcon from './TrashIcon.jsx';
import { filterEvidenceFiles, EVIDENCE_ACCEPT, fileNamesFromEvidenceUrl } from '../lib/evidenceFiles.js';
import {
  createLocalFileObjectUrl,
  evidenceViewerUrl,
  isOpenableAttachmentUrl,
  resolveUploadBlobUrl,
} from '../lib/uploadBlobStore.js';

function fileExtension(name = '') {
  const match = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
}

export function FileTypeIcon({ name }) {
  const ext = fileExtension(name);
  const label =
    ext === 'pdf'
      ? 'PDF'
      : ext === 'doc' || ext === 'docx'
        ? 'DOC'
        : ext === 'xls' || ext === 'xlsx'
          ? 'XLS'
          : ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'gif' || ext === 'webp'
            ? 'IMG'
            : 'FILE';
  return (
    <span className={`evidence-file-type is-${label.toLowerCase()}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" width="18" height="18">
        <path
          d="M7 3.75h7.5L19 8.25V20.25a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.75a1 1 0 0 1 1-1Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path
          d="M14.25 3.75V8.5H19"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
      <span>{label}</span>
    </span>
  );
}

export function ViewableFileName({ name, url, className = 'evidence-file-name', onPreview }) {
  const classes = url ? `${className} is-viewable` : className;
  if (url && onPreview) {
    return (
      <button
        type="button"
        className={classes}
        title={`View ${name}`}
        onClick={(e) => {
          e.stopPropagation();
          onPreview({ name, url });
        }}
      >
        {name}
      </button>
    );
  }
  if (url) {
    return (
      <a
        className={classes}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={`View ${name}`}
        onClick={(e) => e.stopPropagation()}
      >
        {name}
      </a>
    );
  }
  return (
    <span className={className} title={name}>
      {name}
    </span>
  );
}

export function RemoveFileButton({ label, onClick, disabled = false }) {
  return (
    <button
      type="button"
      className="evidence-file-remove"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
    >
      <TrashIcon size={16} />
    </button>
  );
}

function EvidenceFileRow({ entry, url, canRemove, onRemove, onPreview, isActive }) {
  const previewable = Boolean(url && onPreview);

  function openPreview() {
    if (!previewable) return;
    onPreview({ name: entry.name, url });
  }

  const rowClass = [previewable ? 'is-previewable' : '', isActive ? 'is-previewing' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <li
      className={rowClass || undefined}
      onClick={previewable ? openPreview : undefined}
    >
      <FileTypeIcon name={entry.name} />
      <ViewableFileName
        name={entry.name}
        url={url}
        onPreview={previewable ? openPreview : undefined}
      />
      {canRemove ? (
        <span onClick={(e) => e.stopPropagation()}>
          <RemoveFileButton label={`Remove ${entry.name}`} onClick={onRemove} />
        </span>
      ) : null}
    </li>
  );
}

function attachmentLabel(file) {
  return String(file?.name || file?.fileName || file?.filename || file?.id || '').trim();
}

function attachmentUrlByName(attachments = []) {
  const map = new Map();
  for (const file of attachments || []) {
    const key = attachmentLabel(file).toLowerCase();
    if (!key || map.has(key)) continue;
    map.set(key, String(file?.url || file?.href || '').trim());
  }
  return map;
}

/**
 * Evidence file picker with click-to-browse and drag-and-drop.
 * Optional Evidence Locker capacity props block uploads when storage is full.
 * When `disabled` (not New Case), hides the dropzone. Pass `allowRemove` to keep
 * trash controls so files can still be deleted after the demand leaves New Case.
 * `ownerId` + optional live `files` unlock local blob previews.
 */
export default function EvidenceUpload({
  fileNames = [],
  files = [],
  onFilesSelected,
  onRemoveFile,
  onRemoveAttachment,
  attachments = [],
  ownerId = '',
  vaultUrl = '',
  disabled = false,
  allowRemove = null,
  storageBlocked = false,
  storageMessage = '',
  storageWarn = false,
  storageWarnMessage = '',
  onPreviewFile,
  previewName = '',
  onViewerUrls,
}) {
  const inputId = useId();
  const inputRef = useRef(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [urls, setUrls] = useState({});
  const [localFiles, setLocalFiles] = useState({});
  const canRemove = allowRemove == null ? !disabled : Boolean(allowRemove);
  const blocked = disabled || storageBlocked;

  const liveFilesByName = useMemo(() => {
    const map = { ...localFiles };
    for (const file of files || []) {
      const key = String(file?.name || '')
        .trim()
        .toLowerCase();
      if (key && file) map[key] = file;
    }
    return map;
  }, [files, localFiles]);

  const pendingNames = useMemo(
    () => new Set(fileNames.map((name) => String(name || '').trim().toLowerCase()).filter(Boolean)),
    [fileNames]
  );

  const urlByName = useMemo(() => attachmentUrlByName(attachments), [attachments]);

  const vaultNames = useMemo(() => fileNamesFromEvidenceUrl(vaultUrl), [vaultUrl]);
  const vaultViewer = useMemo(() => evidenceViewerUrl(vaultUrl), [vaultUrl]);

  // One combined list: vault/pending uploads + any matter attachments not already listed.
  const evidenceEntries = useMemo(() => {
    const entries = [];
    const seen = new Set();

    for (const name of fileNames || []) {
      const label = String(name || '').trim();
      if (!label) continue;
      const key = label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const matchedUrl = urlByName.get(key) || '';
      entries.push({
        id: `pending:${key}`,
        name: label,
        source: 'pending',
        url: matchedUrl,
        canRemove: true,
      });
    }

    for (const file of attachments || []) {
      const label = attachmentLabel(file);
      if (!label) continue;
      const key = label.toLowerCase();
      if (seen.has(key) || pendingNames.has(key)) continue;
      seen.add(key);
      const kind = String(file?.kind || file?.type || '').trim();
      entries.push({
        id: file.id || `att:${key}`,
        name: label,
        source: 'attachment',
        attachment: file,
        url: file?.url || file?.href || '',
        canRemove: kind.toLowerCase() !== 'demand letter',
      });
    }

    return entries;
  }, [fileNames, attachments, pendingNames, urlByName]);

  const hasFiles = evidenceEntries.length > 0;
  const entryKey = evidenceEntries.map((entry) => `${entry.id}:${entry.url || ''}`).join('\u0000');
  const liveFileKey = Object.keys(liveFilesByName).sort().join('\u0000');

  useEffect(() => {
    let cancelled = false;
    const created = [];

    if (!evidenceEntries.length) {
      setUrls({});
      onViewerUrls?.({});
      return undefined;
    }

    (async () => {
      const next = {};
      for (const entry of evidenceEntries) {
        const key = String(entry.name).toLowerCase();
        const live = liveFilesByName[key];
        if (live) {
          const objectUrl = createLocalFileObjectUrl(live);
          if (objectUrl) {
            created.push(objectUrl);
            next[entry.name] = objectUrl;
            continue;
          }
        }

        if (isOpenableAttachmentUrl(entry.url)) {
          next[entry.name] = entry.url;
          continue;
        }

        const fromEntry = evidenceViewerUrl(entry.url);
        if (fromEntry) {
          next[entry.name] = fromEntry;
          continue;
        }

        if (ownerId) {
          const stored = await resolveUploadBlobUrl('evidence', ownerId, entry.name);
          if (stored) {
            created.push(stored);
            next[entry.name] = stored;
            continue;
          }
        }

        // Vault / pending-upload packages: keep every listed name clickable even
        // when the local blob is missing (demo PDF / shared vault viewer URL).
        if (
          vaultViewer &&
          vaultNames.some((name) => String(name).toLowerCase() === key)
        ) {
          next[entry.name] = vaultViewer;
          continue;
        }

        if (vaultViewer) {
          next[entry.name] = vaultViewer;
        }
      }
      if (!cancelled) {
        setUrls(next);
        onViewerUrls?.(next);
      }
    })();

    return () => {
      cancelled = true;
      for (const url of created) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      }
    };
    // evidenceEntries / live files derived from entryKey + liveFileKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, entryKey, liveFileKey, vaultViewer, vaultNames.join('\u0000')]);

  function takeFiles(fileList) {
    if (blocked) return;
    const accepted = filterEvidenceFiles(fileList);
    if (!accepted.length) return;
    setLocalFiles((prev) => {
      const next = { ...prev };
      for (const file of accepted) {
        const key = String(file?.name || '')
          .trim()
          .toLowerCase();
        if (key) next[key] = file;
      }
      return next;
    });
    onFilesSelected?.(accepted);
  }

  function onInputChange(e) {
    takeFiles(e.target.files);
    e.target.value = '';
  }

  function openPicker() {
    if (blocked) return;
    inputRef.current?.click();
  }

  function onDragEnter(e) {
    if (blocked) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    setDragging(true);
  }

  function onDragLeave(e) {
    if (blocked) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }

  function onDragOver(e) {
    if (blocked) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }

  function onDrop(e) {
    if (blocked) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setDragging(false);
    takeFiles(e.dataTransfer?.files);
  }

  const dropCopy = (() => {
    if (storageBlocked) {
      return {
        title: null,
        body: (
          <>
            {storageMessage ||
              'Evidence Locker is full. Delete files from Evidence Locker or upgrade storage to upload more.'}{' '}
            <Link
              className="text-link strong"
              to="/client/settings?tab=billing"
              onClick={(e) => e.stopPropagation()}
            >
              Billing
            </Link>
            {' · '}
            <Link
              className="text-link strong"
              to="/client/evidence-locker"
              onClick={(e) => e.stopPropagation()}
            >
              Evidence Locker
            </Link>
          </>
        ),
      };
    }
    if (dragging) {
      return { title: 'Drop files to upload', body: null };
    }
    if (hasFiles) {
      return {
        title: null,
        body: (
          <>
            Drop more files here or{' '}
            <button
              type="button"
              className="text-link evidence-dropzone-browse"
              onClick={(e) => {
                e.stopPropagation();
                openPicker();
              }}
            >
              browse
            </button>
            {storageWarn && storageWarnMessage ? (
              <>
                <br />
                <span className="evidence-dropzone-warn">{storageWarnMessage}</span>
              </>
            ) : null}
          </>
        ),
      };
    }
    return {
      title: 'Drag & drop files here',
      body: (
        <>
          or{' '}
          <button
            type="button"
            className="text-link evidence-dropzone-browse"
            onClick={(e) => {
              e.stopPropagation();
              openPicker();
            }}
          >
            browse
          </button>{' '}
          for PDFs, images, Word, or Excel
          {storageWarn && storageWarnMessage ? (
            <>
              <br />
              <span className="evidence-dropzone-warn">{storageWarnMessage}</span>
            </>
          ) : null}
        </>
      ),
    };
  })();

  return (
    <div
      className={`field field-span-2 evidence-upload${disabled ? ' is-locked' : ''}${
        !disabled && storageBlocked ? ' is-vault-full' : ''
      }${!disabled && blocked ? ' is-disabled' : ''}${
        !disabled && hasFiles ? ' has-files' : ''
      }`}
    >
      <span className="field-label-with-tip">
        Evidence
        <EvidenceTypesHelp />
      </span>

      {disabled ? (
        <div className="evidence-locked-summary">
          {hasFiles ? (
            <ul className="evidence-file-list">
              {evidenceEntries.map((entry) => (
                <EvidenceFileRow
                  key={entry.id}
                  entry={entry}
                  url={urls[entry.name]}
                  onPreview={onPreviewFile}
                  isActive={Boolean(previewName) && previewName === entry.name}
                  canRemove={canRemove && entry.canRemove}
                  onRemove={
                    canRemove
                      ? () => {
                          if (entry.source === 'attachment') {
                            onRemoveAttachment?.(entry.attachment);
                            return;
                          }
                          setLocalFiles((prev) => {
                            const next = { ...prev };
                            delete next[String(entry.name).toLowerCase()];
                            return next;
                          });
                          onRemoveFile?.(entry.name);
                        }
                      : undefined
                  }
                />
              ))}
            </ul>
          ) : (
            <span className="field-value">No evidence files on this demand</span>
          )}
          <span className="field-value-note">
            {canRemove
              ? 'New evidence can only be added while this demand is a New Case. You can still remove files here.'
              : 'Evidence can only be added or removed while this demand is a New Case.'}
          </span>
        </div>
      ) : (
        <>
          {hasFiles ? (
            <ul className="evidence-file-list">
              {evidenceEntries.map((entry) => (
                <EvidenceFileRow
                  key={entry.id}
                  entry={entry}
                  url={urls[entry.name]}
                  onPreview={onPreviewFile}
                  isActive={Boolean(previewName) && previewName === entry.name}
                  canRemove={canRemove && entry.canRemove}
                  onRemove={() => {
                    if (entry.source === 'attachment') {
                      onRemoveAttachment?.(entry.attachment);
                      return;
                    }
                    setLocalFiles((prev) => {
                      const next = { ...prev };
                      delete next[String(entry.name).toLowerCase()];
                      return next;
                    });
                    onRemoveFile?.(entry.name);
                  }}
                />
              ))}
            </ul>
          ) : null}

          <div
            className={
              dragging && !blocked
                ? `evidence-dropzone${hasFiles ? ' is-compact' : ''} is-dragging`
                : storageBlocked
                  ? `evidence-dropzone${hasFiles ? ' is-compact' : ''} is-storage-full`
                  : `evidence-dropzone${hasFiles ? ' is-compact' : ''}`
            }
            role="button"
            tabIndex={blocked ? -1 : 0}
            aria-disabled={blocked || undefined}
            onClick={openPicker}
            onKeyDown={(e) => {
              if (blocked) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openPicker();
              }
            }}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            <input
              ref={inputRef}
              id={inputId}
              className="evidence-dropzone-input"
              type="file"
              accept={EVIDENCE_ACCEPT}
              multiple
              tabIndex={-1}
              disabled={blocked}
              onChange={onInputChange}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="evidence-dropzone-copy">
              {dropCopy.title ? <strong>{dropCopy.title}</strong> : null}
              {dropCopy.body ? <span>{dropCopy.body}</span> : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
